import React, { useMemo, useState } from "react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function NpsCalculatorPage() {
  const [promoters, setPromoters] = useState(72);
  const [passives, setPassives] = useState(20);
  const [detractors, setDetractors] = useState(8);

  const total = (Number(promoters) || 0) + (Number(passives) || 0) + (Number(detractors) || 0);
  const score = useMemo(() => {
    if (!total) return 0;
    return Math.round((((Number(promoters) || 0) - (Number(detractors) || 0)) / total) * 100);
  }, [promoters, detractors, total]);

  const tone = score >= 70 ? "World-class" : score >= 50 ? "Excellent" : score >= 30 ? "Good" : score >= 0 ? "Needs work" : "Critical";
  const toneColor = score >= 50 ? "text-emerald-600" : score >= 0 ? "text-amber-600" : "text-rose-600";

  return (
    <MarketingLayout>
      <section className="max-w-5xl mx-auto px-6 py-16">
        <p className="text-xs uppercase tracking-wider font-semibold text-[#F58220]">Free Tool</p>
        <h1 className="mt-2 text-4xl md:text-5xl font-extrabold tracking-tight text-[#1F3A6B]">NPS Calculator</h1>
        <p className="mt-3 text-slate-600 text-lg">Enter your response counts to instantly compute your Net Promoter Score.</p>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <Card className="p-6 border-slate-100 shadow-elev-1">
            <div className="space-y-5">
              <Field label="Promoters (9–10)" testId="calc-promoters" value={promoters} onChange={setPromoters} accent="emerald"/>
              <Field label="Passives (7–8)" testId="calc-passives" value={passives} onChange={setPassives} accent="amber"/>
              <Field label="Detractors (0–6)" testId="calc-detractors" value={detractors} onChange={setDetractors} accent="rose"/>
              <p className="text-sm text-slate-500">Total responses: <span className="font-semibold text-[#1F3A6B]">{total}</span></p>
            </div>
          </Card>

          <Card className="p-8 border-slate-100 shadow-elev-2 bg-gradient-to-br from-white to-slate-50">
            <p className="text-sm uppercase tracking-wider text-slate-500 font-semibold">Your NPS</p>
            <p data-testid="calc-result" className={`mt-2 text-7xl font-extrabold ${toneColor} tabular-nums`}>{score}</p>
            <p className={`mt-2 text-base font-semibold ${toneColor}`}>{tone}</p>

            <div className="mt-6 h-3 rounded-full bg-slate-100 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-1/2"
                style={{
                  width: `${Math.min(50, Math.abs(score) / 2)}%`,
                  transform: score >= 0 ? "none" : "translateX(-100%)",
                  background: score >= 0 ? "linear-gradient(90deg,#10B981,#F58220)" : "linear-gradient(90deg,#DC2657,#F59E0B)",
                  borderRadius: "9999px",
                }}
              />
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300" />
            </div>
            <div className="mt-2 flex justify-between text-xs text-slate-500"><span>−100</span><span>0</span><span>+100</span></div>

            <div className="mt-8 flex gap-3">
              <Link to="/signup"><Button data-testid="calc-signup-btn" className="bg-[#F58220] hover:bg-[#E36F0E] text-white">Track NPS automatically</Button></Link>
              <Link to="/what-is-nps"><Button variant="outline">Learn more</Button></Link>
            </div>
          </Card>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Field({ label, value, onChange, accent, testId }) {
  const colors = { emerald: "border-emerald-200 focus:border-emerald-400", amber: "border-amber-200 focus:border-amber-400", rose: "border-rose-200 focus:border-rose-400" };
  return (
    <div>
      <Label className="text-sm font-semibold text-[#1F3A6B]">{label}</Label>
      <Input
        type="number"
        min="0"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className={`mt-1.5 ${colors[accent]} h-11`}
      />
    </div>
  );
}
